import { useQuery } from '@tanstack/react-query';
import { createApiCall } from 'services';

/**
 * Already RESOLVED by the API: `enabled: true` guarantees both URLs are present, so
 * nothing here re-derives the flag-and-both-URLs rule (`resolveReveal` on the server is
 * its single definition).
 */
export interface CertificateReveal {
  enabled: boolean;
  video_url: string | null;
  poster_url: string | null;
}

const NO_REVEAL: CertificateReveal = {
  enabled: false,
  video_url: null,
  poster_url: null,
};

/**
 * VRG-790 — the reveal for this certificate, fetched ALONGSIDE the certificate itself.
 *
 * ## Why this is a second request and not a field on the certificate
 *
 * The reveal's job is to cover the certificate's load. Its media lives on the product,
 * which arrives inside `GET /certificates/public/:id` — so a player that waited for that
 * response could only START the video once the thing it was meant to mask had already
 * landed. The reveal would then be pure added latency on the scan journey, which
 * `CertificatePage`'s own comments call "the dominant cost in the whole scan journey".
 * That is the difference between a flourish and a tax, and it is the whole reason the
 * API grew a dedicated route.
 *
 * Both requests are fired from the same mount, so the cost is `max(a, b)` rather than
 * `a + b` — the same pattern that page already uses for its scan logs. Three fields
 * against a wide multi-table join means this one reliably wins, and the video starts
 * while the certificate is still in flight.
 *
 * ## Every failure is "no reveal", never an error
 *
 * `retry: false` and `NO_REVEAL` on failure. A reveal is a flourish and the certificate
 * is the product, so a slow or failed pre-flight must cost the flourish and nothing
 * else. Retrying would be actively harmful: by the time a retry resolved, the
 * certificate would have arrived and the reveal would be adding delay rather than hiding
 * it — exactly the behaviour this route exists to avoid. The API already fails open to
 * `enabled: false` on its own side; this is the client half of the same doctrine.
 */
export const useCertificateReveal = (certificateId: string | undefined) =>
  useQuery<CertificateReveal>({
    queryKey: ['certificate-reveal', certificateId],
    enabled: Boolean(certificateId),
    queryFn: async () => {
      try {
        const response = await createApiCall({
          method: 'GET',
          url: `/certificates/public/${certificateId}/reveal`,
        });
        const data = response?.data as Partial<CertificateReveal> | undefined;

        // A non-2xx arrives through `errorHandler` as the response object rather than a
        // throw, so a malformed body — not an exception — is what a failure looks like.
        // Treated as no reveal rather than trusted into a player with a null source.
        if (!data || data.enabled !== true || !data.video_url || !data.poster_url) {
          return NO_REVEAL;
        }

        return {
          enabled: true,
          video_url: data.video_url,
          poster_url: data.poster_url,
        };
      } catch {
        return NO_REVEAL;
      }
    },
    retry: false,
    /*
     * The answer changes only when a brand edits the product, so it is worth holding for
     * the session — a viewer who navigates back to a certificate they already opened
     * should not pay for this again. It is NOT cached across reloads on purpose: the
     * `markRevealSeen` record is what stops a repeat play, and conflating "do you have a
     * reveal" with "have you seen it" in one store would make a brand's newly-published
     * reveal invisible to anyone holding a stale answer.
     */
    staleTime: 5 * 60 * 1000,
    // Nothing here is worth a refetch on tab focus; the page is read, not monitored.
    refetchOnWindowFocus: false,
    /*
     * ⚠️ NO `placeholderData`, deliberately, and the first cut of this hook had it.
     *
     * Seeding `NO_REVEAL` makes `data` immediately `{enabled: false}`, which is
     * indistinguishable from a real answer of "this product has no reveal" — so the
     * player would read "no reveal" on the very first render and skip EVERY reveal, on
     * every certificate, while the query it was waiting for resolved correctly a moment
     * later. The feature would be inert and the request would still be made.
     *
     * `data === undefined` is therefore load-bearing: it is the only thing that
     * distinguishes "not answered yet" from "answered: nothing". The three states are
     * the whole decision, and the consumer needs all three (see `revealState` in
     * CertificatePage).
     */
  });
